import { FC, PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as ClosedEyeIcon } from '@/assets/icons/closed-eye.svg';
import { ReactComponent as EyeIcon } from '@/assets/icons/eye.svg';
import Text from '@/components/Text';
import useDidUpdate from '@/hooks/useDidUpdate';
import * as storage from '@/utils/storage';

const Wrap = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
  border-radius: 4px;
`;

const Header = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
`;

const Footer = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Button = styled.button.attrs({ type: 'button' })`
  height: 38px;
  min-width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  transition: 0.2s ease-out all;
  background-color: transparent;
`;

export const ActionButton = styled(Button)<{ $secondary?: boolean }>`
  padding: 0 32px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Manrope', sans-serif;

  /* GRAY variation */
  ${({ theme, $secondary = false }) =>
    $secondary
      ? css`
          color: ${theme.colors.black};
          border-color: ${theme.colors.lightGray};

          &:hover {
            border-color: ${theme.colors.gray};
          }
        `
      : css`
          color: ${({ theme }) => theme.colors.purple};
          border-color: ${({ theme }) => theme.colors.purple};

          &:hover {
            color: ${({ theme }) => theme.colors.white};
            background: ${({ theme }) => theme.colors.purple};
          }
        `}

  &:disabled {
    color: ${({ theme }) => theme.colors.gray};
    border-color: ${({ theme }) => theme.colors.lightGray};
    cursor: not-allowed;
    background: transparent;
  }
`;

const ToggleButton = styled(Button)<{ $closed: boolean }>`
  border-color: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme, $closed }) =>
    $closed &&
    css`
      background: ${theme.colors.lightGray};
    `}
`;

const Body = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

interface Props {
  title: string;
  description: string;
  defaultIsOpened: boolean;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  storageKey: string;
}

const Card: FC<PropsWithChildren<Props>> = ({
  defaultIsOpened,
  title,
  storageKey,
  description,
  children,
  footer,
  actions = null,
}) => {
  const [isOpened, setIsOpened] = useState(
    storage.get(storageKey) ?? defaultIsOpened ?? false,
  );

  const toggleIsOpened = () => setIsOpened((prev) => !prev);

  useDidUpdate(() => {
    storage.set(storageKey, isOpened);
  }, [isOpened, storageKey]);

  return (
    <Wrap>
      <Header>
        <HeaderContent>
          <Text $type="button">{title}</Text>
          <Text $color="gray" $type="smallHeadline">
            {description}
          </Text>
        </HeaderContent>
        <Actions>
          {actions}
          <ToggleButton $closed={!isOpened} onClick={toggleIsOpened}>
            {isOpened ? <EyeIcon /> : <ClosedEyeIcon />}
          </ToggleButton>
        </Actions>
      </Header>
      {isOpened && (
        <>
          <Body>{children}</Body>
          {!!footer && <Footer>{footer}</Footer>}
        </>
      )}
    </Wrap>
  );
};

export default Card;
